import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/Footer";
import { generatePageTitle, generateCanonicalUrl } from "@/lib/seoConfig";

const DMCA = () => {
  return (
    <>
      <Helmet>
        <title>{generatePageTitle("DMCA Policy")}</title>
        <meta name="description" content="iQ Texting DMCA Policy. Digital Millennium Copyright Act compliance and copyright infringement notification procedures." />
        <link rel="canonical" href={generateCanonicalUrl("/dmca")} />
        <meta property="og:title" content={generatePageTitle("DMCA Policy")} />
        <meta property="og:description" content="Digital Millennium Copyright Act compliance and copyright notification procedures." />
        <meta property="og:url" content={generateCanonicalUrl("/dmca")} />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-6">
          <Button variant="ghost" asChild className="mb-4">
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </Button>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            DMCA Policy
          </h1>
          <p className="text-muted-foreground mt-2">Digital Millennium Copyright Act Notice</p>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-4">Copyright Policy</h2>
            <p className="text-muted-foreground">
              iQ Texting respects the intellectual property rights of others and expects its users to do the same. 
              In accordance with the Digital Millennium Copyright Act of 1998 (DMCA), we will respond expeditiously 
              to claims of copyright infringement committed using our service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Filing a DMCA Notice</h2>
            <p className="text-muted-foreground mb-2">
              If you believe that your copyrighted work has been copied in a way that constitutes copyright 
              infringement and is accessible via our service, please notify our copyright agent with the 
              following information:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>A physical or electronic signature of the copyright owner or authorized agent</li>
              <li>Identification of the copyrighted work claimed to have been infringed</li>
              <li>Identification of the material that is claimed to be infringing, with information reasonably 
              sufficient to permit us to locate the material</li>
              <li>Your contact information (address, telephone number, and email address)</li>
              <li>A statement that you have a good faith belief that use of the material is not authorized by 
              the copyright owner, its agent, or the law</li>
              <li>A statement that the information in the notification is accurate, and under penalty of perjury, 
              that you are authorized to act on behalf of the copyright owner</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Designated Copyright Agent</h2>
            <p className="text-muted-foreground">Our designated agent for notice of claims of copyright infringement is:</p>
            <div className="bg-muted p-4 rounded-lg mt-4">
              <p className="text-muted-foreground">
                <strong>DMCA Agent</strong><br />
                iQ Texting<br />
                123 Business Ave, Suite 100<br />
                San Francisco, CA 94102<br />
                Email: dmca@iqtexting.com<br />
                Phone: 1-800-IQ-TEXTS
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Counter-Notification</h2>
            <p className="text-muted-foreground mb-2">
              If you believe that your content that was removed (or to which access was disabled) is not infringing, 
              or that you have authorization from the copyright owner, the copyright owner's agent, or pursuant to 
              law, to post and use the material, you may send a counter-notice containing the following information:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Your physical or electronic signature</li>
              <li>Identification of the content that has been removed and the location where it appeared</li>
              <li>A statement under penalty of perjury that you have a good faith belief that the content was 
              removed as a result of mistake or misidentification</li>
              <li>Your name, address, telephone number, and email address</li>
              <li>A statement that you consent to the jurisdiction of the federal court in your location and 
              that you will accept service of process from the person who provided the original notification</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Repeat Infringers</h2>
            <p className="text-muted-foreground">
              In accordance with the DMCA and other applicable law, we have adopted a policy of terminating, 
              in appropriate circumstances and at our sole discretion, users who are deemed to be repeat infringers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Misrepresentations</h2>
            <p className="text-muted-foreground">
              Please note that under Section 512(f) of the DMCA, any person who knowingly materially 
              misrepresents that material or activity is infringing, or that material or activity was removed 
              or disabled by mistake or misidentification, may be subject to liability.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Questions</h2>
            <p className="text-muted-foreground">
              If you have questions about this DMCA Policy, please contact us at dmca@iqtexting.com
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
    </>
  );
};

export default DMCA;
